//
//  src/components/security/DeviceKeyFlowManager.tsx
//  TownLIT
//
//  Global silent conversation identity preparation.
//  Recovery UI is handled only at Messenger entry.
//

"use client";

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
} from "../common";

import DeviceLimitModal from "./DeviceLimitModal";

import {
  migrateFromLocalStorageIfNeeded,
} from "../../utils/crypto/keyStorageSecure";

import {
  silentlyPrepareConversationIdentity,
} from "../../services/crypto/conversationEncryptionService";

import {
  logger,
} from "../../utils/logging/logger";

import {
  useAppSelector,
} from "../../redux/hooks";

import {
  selectAuthUser,
} from "../../redux/selectors/profile/userSelectors";

import {
  isOwnerProfileTemporarilyUnavailable,
} from "../../utils/profiles/profileGate";

import {
  useAlertMessage,
} from "../../hooks/common/useAlertMessage";

import "./DeviceKeyFlowManager.css";

export const devLog = (
  ...args: unknown[]
): void => {
  const isDevelopment =
    process.env.NODE_ENV !== "production";

  const verbose =
    process.env.NEXT_PUBLIC_VERBOSE_LOGS ===
    "true";

  if (isDevelopment || verbose) {
    logger.debug(
      "[DeviceKeyFlow]",
      {
        args,
      },
    );
  }
};

export default function DeviceKeyFlowManager() {
  const authUser =
    useAppSelector(selectAuthUser);

  const rawProfile = useAppSelector(
    (state) => state.profile.profile,
  ) as any;

  const isMember =
    Boolean(authUser?.is_member);

  const restrictionResolutionPending =
    Boolean(
      authUser &&
        isMember &&
        !authUser.is_deleted &&
        !authUser.is_suspended &&
        !rawProfile,
    );

  const restrictedProfile =
    Boolean(
      authUser &&
        isMember &&
        rawProfile &&
        isOwnerProfileTemporarilyUnavailable(
          rawProfile,
        ),
    );

  const canPrepareIdentity =
    Boolean(
      authUser &&
        !authUser.is_deleted &&
        !authUser.is_suspended &&
        !restrictionResolutionPending &&
        !restrictedProfile,
    );

  const runningRef =
    useRef(false);

  const completedUserRef =
    useRef<number | null>(null);

  const [
    deviceLimitOpen,
    setDeviceLimitOpen,
  ] = useState(false);

  const [
    deviceLimitCount,
    setDeviceLimitCount,
  ] = useState(10);

  const {
    message,
    showMessage,
    clearMessage,
  } = useAlertMessage();

  useEffect(() => {
    const currentUserID =
      authUser?.id ?? null;

    if (
      !canPrepareIdentity ||
      currentUserID === null
    ) {
      runningRef.current = false;
      completedUserRef.current = null;

      setDeviceLimitOpen(false);

      return;
    }

    if (
      runningRef.current ||
      completedUserRef.current === currentUserID
    ) {
      return;
    }

    let cancelled = false;

    const run = async (): Promise<void> => {
      runningRef.current = true;

      try {
        await migrateFromLocalStorageIfNeeded();

        if (cancelled) {
          return;
        }

        const result =
          await silentlyPrepareConversationIdentity();

        if (cancelled) {
          return;
        }

        completedUserRef.current =
          currentUserID;

        switch (result) {
          case "ready":
            window.dispatchEvent(
              new Event(
                "keys:registered",
              ),
            );

            window.dispatchEvent(
              new Event(
                "keys:state-changed",
              ),
            );

            break;

          case "backup_required":
            // The local identity is usable.
            // Recovery backup remains a Messenger-only gate.
            window.dispatchEvent(
              new Event(
                "keys:registered",
              ),
            );

            window.dispatchEvent(
              new Event(
                "keys:state-changed",
              ),
            );

            break;

          case "restore_required":
            // Do not block the authenticated application.
            // Messenger will present the restore flow.
            devLog(
              "Existing encryption backup requires restoration at Messenger entry.",
            );

            break;

          case "reset_required":
            // Do not create or rotate a key here.
            // Messenger or Settings will present the explicit reset flow.
            devLog(
              "Existing encryption identity requires recovery or explicit reset.",
            );

            break;

          case "recovery_metadata_incomplete":
            // Fail closed for Messenger, but do not block the rest of TownLIT.
            devLog(
              "Encryption recovery metadata is incomplete.",
            );

            break;

          case "unavailable":
            devLog(
              "Silent conversation identity preparation is currently unavailable.",
            );

            break;

          default: {
            const exhaustiveCheck: never =
              result;

            devLog(
              "Unexpected conversation identity preparation result:",
              exhaustiveCheck,
            );
          }
        }
      } catch (error: any) {
        if (cancelled) {
          return;
        }

        const status =
          error?.response?.status;

        const limit =
          error?.response?.data?.limit;

        if (
          status === 403 &&
          typeof limit === "number"
        ) {
          setDeviceLimitCount(limit);
          setDeviceLimitOpen(true);

          showMessage(
            `This account has reached its ${limit}-device encryption limit. Remove an older device before registering this browser.`,
            "warning",
          );

          return;
        }

        const errorCode =
          error?.response?.data?.code;

        if (
          status === 403 &&
          errorCode ===
            "profile_temporarily_unavailable"
        ) {
          devLog(
            "Silent encryption preparation stopped because the profile is temporarily unavailable.",
          );

          return;
        }

        devLog(
          "Silent encryption preparation failed:",
          error?.response?.data ||
            error?.message ||
            error,
        );

        // Do not show a global blocking error.
        // Messenger provides the actionable recovery UI.
      } finally {
        runningRef.current = false;
      }
    };

    void run();

    return () => {
      cancelled = true;
      runningRef.current = false;
    };
  }, [
    authUser?.id,
    canPrepareIdentity,
    showMessage,
  ]);

  return (
    <>
      {message ? (
        <div className="alert-container">
          <Alert
            type={message.type}
            onClose={clearMessage}
            autoCloseDuration={7000}
            closable
          >
            {message.text}
          </Alert>
        </div>
      ) : null}

      <DeviceLimitModal
        isOpen={deviceLimitOpen}
        limit={deviceLimitCount}
        onClose={() => {
          setDeviceLimitOpen(false);
        }}
        onManage={() => {
          setDeviceLimitOpen(false);

          window.location.assign(
            "/settings/crypto-security",
          );
        }}
      />
    </>
  );
}